"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { AngleDiagram } from "@/components/tools/geometry/angle-diagram";
import {
  DiagramDownload,
  useDiagramRef,
} from "@/components/tools/geometry/diagram";
import {
  DECIMAL_PLACES,
  type DecimalPlaces,
  type Format,
  formatMeasure,
} from "@/components/tools/geometry/format";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  ANGLE_UNITS,
  type AngleConversions,
  type AngleMeasure,
  type AngleUnitId,
  convertAngle,
  parseAngleValue,
  parseDmsValue,
} from "@/lib/tool-kit/geometry/angle-convert";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import { formatRationalNice } from "@/lib/tool-kit/math/rational";

type Source = AngleUnitId | "dms";
type Mark = "." | ",";

/** English strings. A translated content file overrides these through `ui`. */
export const ANGLE_CONVERTER_STRINGS = {
  unitLabel: "Convert from",
  units: {
    deg: "Degrees (°)",
    rad: "Radians (rad)",
    grad: "Gradians (gon)",
    turn: "Turns (revolutions)",
    arcmin: "Arcminutes (′)",
    arcsec: "Arcseconds (″)",
    dms: "Degrees, minutes and seconds",
  },
  symbols: {
    deg: "°",
    rad: "rad",
    grad: "gon",
    turn: "turn",
    arcmin: "′",
    arcsec: "″",
  },
  valueLabel: "Angle",
  valueHint:
    "Whole numbers, decimals and fractions such as 1/8. Negative angles and angles over a full turn are fine.",
  radianHint:
    "Type a multiple of π such as pi/6 or 5π/12 to keep the answer exact; a plain number is read as radians.",
  dmsDegrees: "Degrees",
  dmsMinutes: "Minutes",
  dmsSeconds: "Seconds",
  dmsHint:
    "Put a minus sign on the degrees for a negative angle. Minutes and seconds may exceed 59 — they are carried over.",
  decimalsLabel: "Decimal places",
  results: "Results",
  unitHeader: "Unit",
  exactHeader: "Exact",
  decimalHeader: "Decimal",
  rows: {
    deg: "Degrees",
    rad: "Radians",
    grad: "Gradians",
    turn: "Turns",
    arcmin: "Arcminutes",
    arcsec: "Arcseconds",
    dms: "Degrees, minutes, seconds",
    normalized: "Coterminal angle (0° to 360°)",
  },
  workingHeading: "The working",
  figureHeading: "Figure",
  figureTitle:
    "An angle of {angle} on a circle, with the quarter turns marked in degrees, radians and gradians",
  downloadFigure: "Download figure (PNG)",
  copy: "Copy results",
  copied: "Copied!",
  summary: "{angle} = {rad} rad = {grad} gon = {turn} turn = {dms}",
  enter: "Enter an angle to convert it.",
  invalid:
    "Enter the angle as a number or fraction; in radians you can also write multiples of π, such as pi/6.",
  invalidDms:
    "Enter degrees, minutes and seconds as numbers, with no minus sign on the minutes or seconds.",
} as const;

type Strings = typeof ANGLE_CONVERTER_STRINGS;

const ID_PREFIX = "ac";
/** Units whose symbol is written against the number (30°), not after a space (2 rad). */
const ATTACHED = new Set<AngleUnitId>(["deg", "arcmin", "arcsec"]);
const EXAMPLES: Record<AngleUnitId, string> = {
  deg: "30",
  rad: "pi/6",
  grad: "100",
  turn: "1/8",
  arcmin: "90",
  arcsec: "3600",
};

function measureText(
  measure: AngleMeasure,
  mark: Mark,
  fmt: Format,
  places: number
): { exact: string; decimal: string } {
  const decimal = formatMeasure(fmt, measure.value, places);
  const nice = measure.exact ? formatRationalNice(measure.exact, mark) : null;
  return { exact: nice ?? "—", decimal };
}

function dmsTexts(
  result: AngleConversions,
  mark: Mark,
  fmt: Format,
  places: number
): { exact: string; decimal: string } {
  const { dms } = result;
  const sign = dms.negative ? "−" : "";
  const seconds = dms.seconds.exact
    ? formatRationalNice(dms.seconds.exact, mark)
    : formatMeasure(fmt, dms.seconds.value, places);
  const decimalSeconds = formatMeasure(fmt, dms.seconds.value, places);
  return {
    exact: `${sign}${dms.degrees}° ${dms.minutes}′ ${seconds}″`,
    decimal: `${sign}${dms.degrees}° ${dms.minutes}′ ${decimalSeconds}″`,
  };
}

function radianTexts(
  result: AngleConversions,
  fmt: Format,
  places: number
): { exact: string; decimal: string } {
  return {
    exact: result.radiansPi === null ? "—" : result.radiansPi,
    decimal: formatMeasure(fmt, result.radians, places),
  };
}

function workingLines(
  source: Source,
  result: AngleConversions,
  mark: Mark
): string[] {
  const deg = result.deg.exact
    ? formatRationalNice(result.deg.exact, mark)
    : null;
  const shown = deg ?? "θ";
  const exactOr = (measure: AngleMeasure) =>
    measure.exact ? formatRationalNice(measure.exact, mark) : "…";
  const lines = [
    `rad = deg × π / 180 = ${shown} × π / 180 = ${result.radiansPi ?? "…"}`,
    `gon = deg × 10 / 9 = ${shown} × 10 / 9 = ${exactOr(result.grad)}`,
    `turn = deg / 360 = ${shown} / 360 = ${exactOr(result.turn)}`,
    `arcmin = deg × 60 = ${exactOr(result.arcmin)}`,
    `arcsec = deg × 3600 = ${exactOr(result.arcsec)}`,
  ];
  if (source === "dms") {
    lines.unshift(`deg = d + m / 60 + s / 3600 = ${shown}`);
  }
  return lines;
}

function Outcome({
  source,
  text,
  dms,
  strings,
  mark,
  fmt,
  places,
}: {
  source: Source;
  text: string;
  dms: [string, string, string];
  strings: Strings;
  mark: Mark;
  fmt: Format;
  places: number;
}) {
  const svgRef = useDiagramRef();
  const empty =
    source === "dms"
      ? dms.every((part) => part.trim() === "")
      : text.trim() === "";
  if (empty) {
    return <Notice>{strings.enter}</Notice>;
  }
  const value =
    source === "dms"
      ? parseDmsValue(dms[0], dms[1], dms[2])
      : parseAngleValue(source, text);
  if (value === null) {
    return (
      <ErrorNotice>
        {source === "dms" ? strings.invalidDms : strings.invalid}
      </ErrorNotice>
    );
  }
  const result = convertAngle(value);
  const parts = {
    deg: measureText(result.deg, mark, fmt, places),
    rad: radianTexts(result, fmt, places),
    grad: measureText(result.grad, mark, fmt, places),
    turn: measureText(result.turn, mark, fmt, places),
    arcmin: measureText(result.arcmin, mark, fmt, places),
    arcsec: measureText(result.arcsec, mark, fmt, places),
    dms: dmsTexts(result, mark, fmt, places),
    normalized: measureText(result.normalized, mark, fmt, places),
  };
  const symbol = strings.symbols;
  const attach = (text: string, unit: AngleUnitId) =>
    ATTACHED.has(unit) ? `${text}${symbol[unit]}` : `${text} ${symbol[unit]}`;
  const withUnit = (
    unit: AngleUnitId,
    texts: { exact: string; decimal: string }
  ) => [
    strings.rows[unit],
    texts.exact === "—" ? "—" : attach(texts.exact, unit),
    attach(texts.decimal, unit),
  ];
  const rows = [
    withUnit("deg", parts.deg),
    withUnit("rad", parts.rad),
    withUnit("grad", parts.grad),
    withUnit("turn", parts.turn),
    withUnit("arcmin", parts.arcmin),
    withUnit("arcsec", parts.arcsec),
    [strings.rows.dms, parts.dms.exact, parts.dms.decimal],
    [
      strings.rows.normalized,
      `${parts.normalized.exact}°`,
      `${parts.normalized.decimal}°`,
    ],
  ];
  const radiansShown = result.radiansPi ?? parts.rad.decimal;
  const summary = t(strings.summary, {
    angle: `${parts.deg.exact === "—" ? parts.deg.decimal : parts.deg.exact}°`,
    rad: radiansShown,
    grad: parts.grad.exact === "—" ? parts.grad.decimal : parts.grad.exact,
    turn: parts.turn.exact === "—" ? parts.turn.decimal : parts.turn.exact,
    dms: parts.dms.exact,
  });
  const legend = [
    `${parts.deg.exact === "—" ? parts.deg.decimal : parts.deg.exact}°`,
    `${radiansShown} ${symbol.rad}`,
    `${parts.grad.decimal} ${symbol.grad}`,
    `${parts.turn.decimal} ${symbol.turn}`,
  ];
  return (
    <>
      <DataTable
        head={[strings.unitHeader, strings.exactHeader, strings.decimalHeader]}
        rows={rows}
      />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={summary}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        {workingLines(source, result, mark).map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
      <h3 className={WIDGET_H3}>{strings.figureHeading}</h3>
      <div className="space-y-2">
        <AngleDiagram
          degrees={result.normalized.value}
          legend={legend}
          svgRef={svgRef}
          title={t(strings.figureTitle, { angle: legend[0] })}
        />
        <DiagramDownload
          fileName="angle-conversion"
          label={strings.downloadFigure}
          svgRef={svgRef}
        />
      </div>
    </>
  );
}

export default function AngleConverterWidget() {
  const strings = useToolUi(ANGLE_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const [source, setSource] = useState<Source>("deg");
  const [text, setText] = useState("30");
  const [dms, setDms] = useState<[string, string, string]>(["40", "26", "46"]);
  const [places, setPlaces] = useState<DecimalPlaces>("4");
  const options = [...ANGLE_UNITS, "dms"] as const;

  function changeSource(next: Source) {
    setSource(next);
    if (next !== "dms") {
      setText(EXAMPLES[next]);
    }
  }

  function setPart(index: number, value: string) {
    const next: [string, string, string] = [dms[0], dms[1], dms[2]];
    next[index] = value;
    setDms(next);
  }

  return (
    <>
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectField
            id={`${ID_PREFIX}-unit`}
            label={strings.unitLabel}
            onChange={changeSource}
            options={options.map((id) => ({
              value: id,
              label: strings.units[id],
            }))}
            value={source}
          />
          {source === "dms" ? null : (
            <TextField
              hint={source === "rad" ? strings.radianHint : strings.valueHint}
              id={`${ID_PREFIX}-value`}
              label={strings.valueLabel}
              mono
              onChange={setText}
              value={text}
            />
          )}
          <SelectField
            id={`${ID_PREFIX}-places`}
            label={strings.decimalsLabel}
            onChange={setPlaces}
            options={DECIMAL_PLACES.map((value) => ({ value, label: value }))}
            value={places}
          />
        </div>
        {source === "dms" ? (
          <div className="space-y-2">
            <div className="grid gap-4 sm:grid-cols-3">
              <TextField
                id={`${ID_PREFIX}-d`}
                label={strings.dmsDegrees}
                mono
                onChange={(value) => setPart(0, value)}
                value={dms[0]}
              />
              <TextField
                id={`${ID_PREFIX}-m`}
                label={strings.dmsMinutes}
                mono
                onChange={(value) => setPart(1, value)}
                value={dms[1]}
              />
              <TextField
                id={`${ID_PREFIX}-s`}
                label={strings.dmsSeconds}
                mono
                onChange={(value) => setPart(2, value)}
                value={dms[2]}
              />
            </div>
            <p className="text-muted-foreground text-xs">{strings.dmsHint}</p>
          </div>
        ) : null}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome
          dms={dms}
          fmt={fmt}
          mark={mark}
          places={Number(places)}
          source={source}
          strings={strings}
          text={text}
        />
      </section>
    </>
  );
}
